"use client";
import usePost from "@/hooks/usePost";
import { useLoggedIn } from "@/store/useAuthStore";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

export const redirectedPathNamee = (
  searchParams: any,
  pathName: any,
  locale: string
) => {
  if (!pathName) return "/";
  const segments = pathName.split("/");
  segments[1] = locale;

  if (searchParams?.toString()?.length > 1) {
    // Clone searchParams so we don’t mutate the original
    const newParams = new URLSearchParams(searchParams);

    // size/color are language-specific variant labels and don't map across
    // languages, so drop them on switch. `category` (subcategory filter on
    // category pages) IS language-specific too, but callers should call
    // translateCategoryFilter first to remap it to the target-language
    // label rather than losing the user's filter.
    newParams.delete("size");
    newParams.delete("color");

    const query = newParams.toString();

    return query ? segments.join("/") + `?${query}` : segments.join("/");
  }

  return segments.join("/");
};

// Subcategory filter values in the URL (`?category=Tents`) are the
// language-specific labels returned by the backend. When switching language,
// the EN label won't match anything in the AR data, so previously this filter
// was wiped. Instead, look up the matching subcategory by `_id` in both
// languages and substitute the label so the user lands on the same filtered
// page in the new language.
export const translateCategoryFilter = async (
  searchParams: any,
  pathName: any,
  targetLang: string,
  PostMethod: any
): Promise<URLSearchParams> => {
  const params = new URLSearchParams(searchParams || "");
  const categoryParam = params.get("category");
  if (!categoryParam) return params;

  // pathName segments: ["", "<lang>", "category", "<parentSlug>", ...]
  const segments = (pathName || "").split("/");
  if (segments[2] !== "category" || !segments[3]) return params;

  const currentLang = segments[1];
  if (!currentLang || currentLang === targetLang) return params;

  const parentSlug = segments[3];
  const currentLabels = categoryParam
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (currentLabels.length === 0) return params;

  try {
    const [currentRes, targetRes] = await Promise.all([
      PostMethod({
        method: "get",
        language: currentLang,
        url: `catSubcat/subcategory/${parentSlug}`,
      }),
      PostMethod({
        method: "get",
        language: targetLang,
        url: `catSubcat/subcategory/${parentSlug}`,
      }),
    ]);

    if (!currentRes?.success || !targetRes?.success) return params;

    const currentList: any[] = Array.isArray(currentRes.data)
      ? currentRes.data
      : [];
    const targetList: any[] = Array.isArray(targetRes.data)
      ? targetRes.data
      : [];

    const labelToId = new Map<string, string>();
    for (const item of currentList) {
      const label = item?.subCategory || item?.category;
      if (label && item?._id) labelToId.set(label, String(item._id));
    }
    const idToLabel = new Map<string, string>();
    for (const item of targetList) {
      const label = item?.subCategory || item?.category;
      if (label && item?._id) idToLabel.set(String(item._id), label);
    }

    const translated = currentLabels
      .map((label) => {
        const id = labelToId.get(label);
        return id ? idToLabel.get(id) || null : null;
      })
      .filter((l): l is string => !!l);

    if (translated.length > 0) {
      params.set("category", translated.join(","));
    } else {
      // Couldn't translate any label — drop the filter rather than land
      // on a zero-results page with a stale foreign-language filter.
      params.delete("category");
    }
  } catch {
    // network/API failure — fall through with the original params; the
    // existing UI will show zero results and the user can reselect, which
    // is the same behavior as today.
  }

  return params;
};

export const LangSetter = async (
  auth: any,
  PostMethod: any,
  searchParams: any,
  pathName: any,
  router: any,
  lang: string
) => {
  const translatedParams = await translateCategoryFilter(
    searchParams,
    pathName,
    lang,
    PostMethod
  );
  const link = redirectedPathNamee(translatedParams, pathName, lang);
  if (!auth) {
    return router.replace(link);
  }
  try {
    let response = await PostMethod({
      method: "get",
      language: lang,
      url: "user/updateLanguage",
    });

    if (response?.success) {
      console.log("Lang updated successfully : ", lang);
    } else {
      console.log("Lang updation failed");
    }
  } catch (error) {
    console.log("Lang updation failed : ", error);
  }
  return router.replace(link);
};

const LangSwitch = () => {
  const { auth } = useLoggedIn();
  const { PostMethod } = usePost();
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  return (
    <>
      <div className="flex flex-col justify-evenly gap-x-5 mt-2">
        <div
          className="cursor-pointer"
          onClick={() =>
            LangSetter(auth, PostMethod, searchParams, pathName, router, "en")
          }
        >
          <div className="text-base h-[34px] font-medium relative flex items-center">
            {pathName?.split("/").includes("en") ? (
              <span className="absolute h-2/4 top-1/2 transform -translate-y-1/2 left-0 w-[3px] bg-primary"></span>
            ) : null}
            <p className="inline-block pl-[6px]">English</p>
          </div>
        </div>
        <div
          className="cursor-pointer"
          onClick={() =>
            LangSetter(auth, PostMethod, searchParams, pathName, router, "ar")
          }
        >
          <div className="text-base h-[34px] font-medium relative flex items-center">
            {pathName?.split("/").includes("ar") ? (
              <span className="absolute h-2/4 top-1/2 transform -translate-y-1/2 right-0 w-[3px] bg-primary"></span>
            ) : null}
            <p className="inline-block pr-[6px]"> العربية </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default LangSwitch;
